<template>
  <div>
    <div class="adm-toolbar">
      <div class="adm-search">
        <i class="fas fa-search"></i>
        <input v-model.trim="search" class="adm-input" placeholder="Search name, email or clinic" aria-label="Search clinic logins" />
      </div>
      <div class="adm-toolbar__spacer"></div>
      <button type="button" class="adm-btn adm-btn--primary" @click="openCreate()">
        <i class="fas fa-user-plus"></i> Add clinic login
      </button>
    </div>

    <div class="adm-alert adm-alert--info">
      <i class="fas fa-info-circle"></i>
      <div>Clinic staff sign in on the <strong>Doctor login</strong> page with their email. They see the appointments of the doctors you assign to them.</div>
    </div>

    <div class="adm-card">
      <div class="adm-table-wrap">
        <table class="adm-table">
          <thead>
            <tr>
              <th>Staff member</th>
              <th>Clinic &amp; doctors</th>
              <th>Last sign-in</th>
              <th>Status</th>
              <th class="adm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="loading">
              <tr v-for="n in 3" :key="`s${n}`">
                <td v-for="c in 4" :key="c"><div class="adm-skeleton" style="width: 70%"></div></td>
                <td></td>
              </tr>
            </template>
            <tr v-else-if="!filtered.length">
              <td colspan="5">
                <div class="adm-empty">
                  <i class="fas fa-id-badge"></i>
                  <strong>{{ search ? "No clinic login matches your search" : "No clinic logins yet" }}</strong>
                  <template v-if="!search">Create one so a clinic's front desk can manage its appointments.</template>
                </div>
              </td>
            </tr>
            <template v-else>
              <tr v-for="user in filtered" :key="user.clinicuserid">
                <td>
                  <div class="adm-person">
                    <span class="adm-avatar">
                      <template v-if="user.name || user.email">{{ initials(user.name || user.email) }}</template>
                      <i v-else class="fas fa-id-badge"></i>
                    </span>
                    <span>
                      <span class="adm-person__name">{{ user.name || "Clinic login" }}</span>
                      <span class="adm-person__meta">{{ user.email || `ID ${user.clinicuserid}` }}</span>
                    </span>
                  </div>
                </td>
                <td>
                  <div v-for="clinic in user.clinics" :key="clinic.clinicid" class="adm-cu-clinic">
                    <span class="adm-strong">{{ clinic.clinicname || clinic.clinicid }}</span>
                    <div>
                      <span v-for="doctor in clinic.doctors" :key="doctor.doctorid" class="adm-chip">{{ doctor.doctorname }}</span>
                    </div>
                  </div>
                </td>
                <td class="adm-muted adm-nowrap">{{ user.has_login ? formatDateTime(user.last_login) : "—" }}</td>
                <td>
                  <span v-if="!user.has_login" class="adm-badge adm-badge--pending" title="Signs in through the old Cognito login until moved">Not moved yet</span>
                  <span v-else class="adm-badge" :class="user.is_active ? 'adm-badge--active' : 'adm-badge--inactive'">
                    {{ user.is_active ? "Active" : "Disabled" }}
                  </span>
                </td>
                <td class="adm-col-actions">
                  <button type="button" class="adm-btn adm-btn--sm" @click="openEdit(user)">
                    <i class="fas fa-user-md"></i> Doctors
                  </button>
                  <template v-if="user.has_login">
                    <button type="button" class="adm-btn adm-btn--sm" @click="resetting = { user, password: '', error: '' }">Reset password</button>
                    <button type="button" class="adm-btn adm-btn--sm"
                            @click="user.is_active ? (confirm = { user, action: 'disable' }) : setActive(user, true)">
                      {{ user.is_active ? "Disable" : "Enable" }}
                    </button>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Create -->
    <admin-modal v-if="creating" title="Add clinic login" subtitle="The login works straight away with the password you set."
                 :busy="saving" @close="creating = false">
      <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>
      <div class="adm-grid">
        <div class="adm-field">
          <label class="adm-label" for="cu-name">Full name <span class="adm-req">*</span></label>
          <input id="cu-name" v-model="form.name" class="adm-input" :class="{ 'is-invalid': errors.name }" maxlength="100" placeholder="Priya Shetty" />
          <div v-if="errors.name" class="adm-field__error">{{ errors.name }}</div>
        </div>
        <div class="adm-field">
          <label class="adm-label" for="cu-email">Email <span class="adm-req">*</span></label>
          <input id="cu-email" v-model="form.email" type="email" class="adm-input" :class="{ 'is-invalid': errors.email }" maxlength="150" placeholder="frontdesk@clinic.in" />
          <div v-if="errors.email" class="adm-field__error">{{ errors.email }}</div>
        </div>
        <div class="adm-span-all">
          <password-field v-model="form.password" :error="errors.password" />
        </div>
        <div class="adm-field adm-span-all">
          <label class="adm-label" for="cu-clinic">Clinic <span class="adm-req">*</span></label>
          <select id="cu-clinic" v-model="form.clinicid" class="adm-select" :class="{ 'is-invalid': errors.clinicid }" @change="loadClinicDoctors($event.target.value)">
            <option value="" disabled>Select clinic</option>
            <option v-for="clinic in clinics" :key="clinic.clinicid" :value="clinic.clinicid">
              {{ clinic.clinicname }} · {{ clinic.locationname || clinic.clinicid }}
            </option>
          </select>
          <div v-if="errors.clinicid" class="adm-field__error">{{ errors.clinicid }}</div>
        </div>
      </div>
      <doctor-picker v-if="form.clinicid" v-model="form.doctorids" :doctors="clinicDoctors" :error="errors.doctorids"
                     @go-clinics="creating = false; $emit('navigate', 'clinics')" />
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="creating = false">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="create">
          <span v-if="saving" class="adm-spinner"></span> Create login
        </button>
      </template>
    </admin-modal>

    <!-- Edit doctors -->
    <admin-modal v-if="editing" :title="`Doctors for ${editing.user.name || editing.user.email}`"
                 subtitle="Choose whose appointments this login can see." :busy="saving" @close="editing = null">
      <div v-if="formError" class="adm-alert adm-alert--error"><i class="fas fa-exclamation-circle"></i><div>{{ formError }}</div></div>
      <div v-if="editing.user.clinics.length > 1" class="adm-field">
        <label class="adm-label" for="cu-edit-clinic">Clinic</label>
        <select id="cu-edit-clinic" v-model="editing.clinicid" class="adm-select" @change="selectEditClinic($event.target.value)">
          <option v-for="clinic in editing.user.clinics" :key="clinic.clinicid" :value="clinic.clinicid">{{ clinic.clinicname }}</option>
        </select>
      </div>
      <doctor-picker v-model="editing.doctorids" :doctors="clinicDoctors" :error="errors.doctorids"
                     @go-clinics="editing = null; $emit('navigate', 'clinics')" />
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="editing = null">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="saveDoctors">
          <span v-if="saving" class="adm-spinner"></span> Save
        </button>
      </template>
    </admin-modal>

    <!-- Set a new password -->
    <admin-modal v-if="resetting" :title="`New password for ${resetting.user.name || resetting.user.email}`"
                 subtitle="They are signed out everywhere and must use the new password." :busy="saving" @close="resetting = null">
      <password-field v-model="resetting.password" label="New password" :error="resetting.error" />
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="resetting = null">Cancel</button>
        <button type="button" class="adm-btn adm-btn--primary" :disabled="saving" @click="resetPassword">
          <span v-if="saving" class="adm-spinner"></span> Set password
        </button>
      </template>
    </admin-modal>

    <!-- Confirm disable -->
    <admin-modal v-if="confirm" size="sm" :busy="saving" title="Disable clinic login?" @close="confirm = null">
      <p class="adm-confirm-text">
        <strong>{{ confirm.user.name || confirm.user.email }}</strong> will be signed out and cannot sign in until you enable the login again.
      </p>
      <template #footer>
        <button type="button" class="adm-btn" :disabled="saving" @click="confirm = null">Cancel</button>
        <button type="button" class="adm-btn adm-btn--danger" :disabled="saving" @click="setActive(confirm.user, false)">
          <span v-if="saving" class="adm-spinner"></span> Disable
        </button>
      </template>
    </admin-modal>
  </div>
</template>

<script>
import axios from "axios";
import AdminModal from "./AdminModal.vue";
import DoctorPicker from "./DoctorPicker.vue";
import PasswordField, { passwordError } from "./PasswordField.vue";
import { EMAIL_RE, errorMessage, formatDateTime, initials } from "../admin-utils";

export default {
  name: "ClinicUsersPanel",
  // The shell passes every panel the same props; ignore the ones this panel does not use.
  inheritAttrs: false,
  components: { AdminModal, DoctorPicker, PasswordField },
  props: {
    intent: { type: Object, default: null },
  },
  emits: ["notify", "changed", "navigate"],
  data() {
    return {
      users: [],
      clinics: [],
      loading: false,
      search: "",
      saving: false,
      creating: false,
      editing: null,
      confirm: null,
      resetting: null,
      form: { name: "", email: "", password: "", clinicid: "", doctorids: [] },
      errors: {},
      formError: "",
      clinicDoctors: null,
    };
  },
  computed: {
    filtered() {
      const term = this.search.toLowerCase();
      if (!term) return this.users;
      return this.users.filter(u =>
        [u.name, u.email, ...u.clinics.map(c => c.clinicname)].some(v => (v || "").toLowerCase().includes(term)));
    },
  },
  watch: {
    // A stale "enter a password" error should go once one is typed or generated.
    "form.password"() {
      if (this.errors.password) this.errors = { ...this.errors, password: "" };
    },
    "resetting.password"() {
      if (this.resetting) this.resetting.error = "";
    },
  },
  methods: {
    initials,
    formatDateTime,
    async fetchAll() {
      this.loading = true;
      try {
        const [users, clinics] = await Promise.all([axios.get("/admin/clinic-users"), axios.get("/admin/clinics")]);
        this.users = users.data || [];
        this.clinics = clinics.data || [];
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not load clinic logins."), "error");
      } finally {
        this.loading = false;
      }
    },
    async loadClinicDoctors(clinicid) {
      this.clinicDoctors = null;
      if (this.creating) this.form.doctorids = [];
      try {
        const { data } = await axios.get(`/admin/clinics/${encodeURIComponent(clinicid)}/doctors`);
        this.clinicDoctors = data || [];
        if (this.creating && this.clinicDoctors.length === 1) this.form.doctorids = [this.clinicDoctors[0].doctorid];
      } catch (error) {
        this.clinicDoctors = [];
        this.formError = errorMessage(error, "Could not load the clinic's doctors.");
      }
    },
    openCreate(clinicid = "") {
      this.form = { name: "", email: "", password: "", clinicid, doctorids: [] };
      this.errors = {};
      this.formError = "";
      this.clinicDoctors = null;
      this.creating = true;
      if (clinicid) this.loadClinicDoctors(clinicid);
    },
    async create() {
      const errors = {};
      if (!this.form.name.trim()) errors.name = "Enter the staff member's name.";
      if (!EMAIL_RE.test(this.form.email.trim())) errors.email = "Enter a valid email.";
      if (passwordError(this.form.password)) errors.password = passwordError(this.form.password);
      if (!this.form.clinicid) errors.clinicid = "Select a clinic.";
      else if (!this.form.doctorids.length) errors.doctorids = "Select at least one doctor.";
      this.errors = errors;
      if (Object.keys(errors).length) return;

      this.saving = true;
      this.formError = "";
      try {
        const { data } = await axios.post("/admin/clinic-users", {
          name: this.form.name.trim(),
          email: this.form.email.trim(),
          password: this.form.password,
          clinicid: this.form.clinicid,
          doctorids: this.form.doctorids,
        });
        this.$emit("notify", `Clinic login created. ${data.email} can sign in now with the password you set.`);
        this.creating = false;
        this.$emit("changed");
        await this.fetchAll();
      } catch (error) {
        this.formError = errorMessage(error, "Could not create the clinic login.");
      } finally {
        this.saving = false;
      }
    },
    openEdit(user) {
      this.errors = {};
      this.formError = "";
      const clinic = user.clinics[0];
      this.editing = { user, clinicid: clinic.clinicid, doctorids: clinic.doctors.map(d => d.doctorid) };
      this.loadClinicDoctors(clinic.clinicid);
    },
    selectEditClinic(clinicid) {
      const clinic = this.editing.user.clinics.find(c => c.clinicid === clinicid);
      this.editing.doctorids = clinic.doctors.map(d => d.doctorid);
      this.loadClinicDoctors(clinic.clinicid);
    },
    async saveDoctors() {
      this.errors = this.editing.doctorids.length ? {} : { doctorids: "Select at least one doctor." };
      if (this.errors.doctorids) return;
      this.saving = true;
      this.formError = "";
      try {
        await axios.put(`/admin/clinic-users/${encodeURIComponent(this.editing.user.clinicuserid)}/doctors`, {
          clinicid: this.editing.clinicid,
          doctorids: this.editing.doctorids,
        });
        this.$emit("notify", "Doctors updated.");
        this.editing = null;
        await this.fetchAll();
      } catch (error) {
        this.formError = errorMessage(error, "Could not save the doctors.");
      } finally {
        this.saving = false;
      }
    },
    async resetPassword() {
      const { user, password } = this.resetting;
      this.resetting.error = passwordError(password);
      if (this.resetting.error) return;
      this.saving = true;
      try {
        await axios.post(`/admin/clinic-users/${encodeURIComponent(user.clinicuserid)}/reset-password`, { password });
        this.$emit("notify", `Password changed for ${user.email}.`);
        this.resetting = null;
      } catch (error) {
        this.resetting.error = errorMessage(error, "Could not set the password.");
      } finally {
        this.saving = false;
      }
    },
    async setActive(user, active) {
      this.saving = true;
      try {
        await axios.patch(`/admin/clinic-users/${encodeURIComponent(user.clinicuserid)}/status`, { is_active: active });
        this.$emit("notify", `${user.name || user.email} was ${active ? "enabled" : "disabled"}.`);
        this.confirm = null;
        await this.fetchAll();
      } catch (error) {
        this.$emit("notify", errorMessage(error, "Could not update the login."), "error");
      } finally {
        this.saving = false;
      }
    },
  },
  created() {
    this.fetchAll();
    if (this.intent && this.intent.create !== undefined) {
      this.openCreate(typeof this.intent.create === "string" ? this.intent.create : "");
    }
  },
};
</script>

<style scoped>
.adm-cu-clinic + .adm-cu-clinic {
  margin-top: 8px;
}

.adm-confirm-text {
  margin: 0;
}
</style>
